import type { JSX } from 'react';
import { GraduationCap, Plus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useCandidateEducations } from '../hooks/useCandidateEducations';
import { EducationList } from './EducationList';
import { EducationFormDialog } from './EducationFormDialog';

interface CandidateEducationsSectionProps {
  candidateId: string;
}

function EducationsHeader({ onAdd }: { onAdd: () => void }): JSX.Element {
  return (
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
      <CardTitle className="flex items-center gap-2 text-lg">
        <GraduationCap className="h-5 w-5" />
        Education
      </CardTitle>
      <Button variant="outline" size="sm" onClick={onAdd}>
        <Plus className="h-4 w-4 mr-1" />
        Add Education
      </Button>
    </CardHeader>
  );
}

export function CandidateEducationsSection({
  candidateId,
}: CandidateEducationsSectionProps): JSX.Element {
  const {
    educations,
    hasData,
    isLoading,
    isError,
    isCreateDialogOpen,
    setIsCreateDialogOpen,
    editingEducation,
    setEditingEducation,
    handleDelete,
  } = useCandidateEducations(candidateId);

  return (
    <Card>
      <EducationsHeader onAdd={() => setIsCreateDialogOpen(true)} />
      <CardContent>
        <EducationList
          educations={educations}
          hasData={hasData}
          isLoading={isLoading}
          isError={isError}
          onEdit={setEditingEducation}
          onDelete={handleDelete}
          onAdd={() => setIsCreateDialogOpen(true)}
        />
      </CardContent>

      <EducationFormDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        candidateId={candidateId}
        mode="create"
      />

      <EducationFormDialog
        open={!!editingEducation}
        onOpenChange={(open) => !open && setEditingEducation(null)}
        candidateId={candidateId}
        mode="edit"
        education={editingEducation}
      />
    </Card>
  );
}
